import { useMemo, Fragment, useCallback, ReactNode, useState, useRef, useEffect } from 'react';
import Popover from '@glu/popover-react';
import { CaretDownAshIcon, InfoCircleAshIcon } from '@glu/icons-react';
import locale from '@glu/locale';
import classNames from 'classnames';
import useStyles from './SavedViews.styles';
import SaveOrUpdateView from '../SaveOrUpdateView/SaveOrUpdateView';
import ViewRow from '../ViewRow/ViewRow';

export interface IData {
  [key: string]: any;
}

export interface IView {
  /** The ID of the view. Should be unique */
  id: string;
  /** The display name of the view */
  name: string;
  /**
   * Indicates whether the view is the default view.
   * If isDefault is true, Default text will be shown to the right of the display name
   */
  isDefault?: boolean;
  /**
   * Indicates whether the view is static. If isStatic is true, you will not have the ability to
   * rename or delete the view.
   */
  isStatic?: boolean;
  /** The id of the header to map this view to. The view will show up under the associated header */
  headerId?: string;
  /** Whether or not the view is active by default */
  isActive?: boolean;
  /** The data associated with the view. Can be anything */
  data: IData;
}

export interface IHeader {
  /** The header id. Should be unique */
  id: string;
  /** The display name of the header */
  name: string;
  /** A message to show if the group is empty of views */
  empty?: ReactNode;
}

/**
 * Note all callbacks in this interface return a promise. This enables async stores for the data.
 * onCloseSaveError is unique in that it's only a callback for a button click.
 */
export interface ISavedViewProps {
  /** The view that is currently active */
  activeView: IView;
  /** The views available to choose from in the popover */
  views: IView[];
  /** If organizing the views into groups, this is is the list of headers */
  headers?: IHeader[];
  /** Indicates whether or not the active view has diverged from its saved data */
  diverged?: boolean;
  /**
   * Error message to show when something goes wrong during deletion, renaming, setting default.
   * Should be falsy if there is no error to show.
   */
  errorOnAction?: ReactNode;
  /**
   * Error message to show when something goes wrong during saving or creating a view. Should be
   * falsy if there is no error to show
   */
  errorOnSave?: ReactNode;
  /** Indicates whether or not the active view has just been saved */
  wasJustSaved?: boolean;
  /** Callback to handle setting a new view as active. Passed back the view to be made active. */
  onSetActiveSavedView({ onResolve, view }: { view: IView; onResolve(): void }): Promise<void>;
  /** Callback to handle setting a view to default. Passed back the view to be made the default. */
  onSetDefaultSavedView({ view }: { view: IView }): Promise<void>;
  /** Callback to handle the saving of a view. Passed back the view to be saved. */
  onSaveSavedView({ view }: { view: IView }): Promise<void>;
  /** Callback to handle renaming a view. Passed back the view to be renamed and the new name. */
  onRenameSavedView({ name, view }: { view: IView; name: IView['name'] }): Promise<void>;
  /** Callback to handle deleting a view. Passed the view to be deleted */
  onDeleteSavedView({ view }: { view: IView }): Promise<void>;
  /** Callback to handle creating a view. Passed the name of the new view */
  onCreateSavedView({ name }: { name: IView['name'] }): Promise<void>;
  /** the default value used for the input when creating a new view */
  suggestedName?: string;
  /** the max number of saved views possible */
  maxViewsCount?: number;
  /** CSS classes applied to the root element of this component. */
  className?: string;
  /** Html id attribute applied to the root element of this component. */
  htmlId?: string;
}

const popperOptions = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 5],
      },
    },
  ],
};

const SavedViews = ({
  activeView,
  className,
  diverged,
  errorOnAction,
  errorOnSave,
  headers,
  htmlId,
  maxViewsCount,
  onCreateSavedView,
  onDeleteSavedView,
  onRenameSavedView,
  onSaveSavedView,
  onSetActiveSavedView,
  onSetDefaultSavedView,
  suggestedName,
  views,
  wasJustSaved,
}: ISavedViewProps) => {
  const [open, setOpen] = useState(false);
  const classes = useStyles();
  const popoverContentRef = useRef<HTMLUListElement>(null);

  const handleSavedViewsClick = () =>
    setOpen((currentlyOpen) => {
      return !currentlyOpen;
    });

  const groupedViews = useMemo(() => {
    if (headers) {
      return headers.reduce<(IHeader & { views: IView[] })[]>(
        (gv, h) => [
          ...gv,
          {
            ...h,
            views: views.filter((v) => v.headerId === h.id),
          },
        ],
        []
      );
    }
    return [
      {
        id: 'default-group',
        name: undefined,
        views,
        empty: undefined,
      },
    ];
  }, [headers, views]);

  const popoverContent = useCallback(() => {
    const handleActionKeyDown = (event) => {
      const { key, target } = event;
      // Gets the targeted view id passed by consumer than finds corresponding index
      const viewId = target.id.split('-action')[0];
      const viewIndex = views.findIndex((view) => view.id === viewId);
      // The view button associated with the targeted action button
      const viewButton = document.getElementById(`${viewId}-button`);
      switch (key) {
        case 'ArrowDown':
          if (viewIndex !== views.length - 1) {
            const nextView = views[viewIndex + 1];
            const nextAction = document.getElementById(`${nextView.id}-action`);
            nextAction!.focus();
          }
          break;
        case 'ArrowUp':
          if (viewIndex !== 0) {
            const previousView = views[viewIndex - 1];
            // If the first view is default and static this will return undefined
            const previousAction = document.getElementById(`${previousView.id}-action`);
            previousAction?.focus();
          }
          break;
        case 'ArrowLeft':
          viewButton!.focus();
          break;
        case 'Tab':
        case 'Escape':
          event.preventDefault();
          setOpen(false);
          break;
        default:
      }
    };

    const handleViewKeyDown = (event) => {
      const { key, target } = event;
      // Gets the targeted view id passed by consumer than finds corresponding index
      const viewId = target.id.split('-button')[0];
      const viewIndex = views.findIndex((view) => view.id === viewId);
      // The action button associated with the targeted view button
      // May be undefined if view is static and default
      const viewAction = document.getElementById(`${viewId}-action`);
      switch (key) {
        case 'ArrowDown':
          if (viewIndex !== views.length - 1) {
            const nextView = views[viewIndex + 1];
            const nextButton = document.getElementById(`${nextView.id}-button`);
            nextButton!.focus();
          }
          break;
        case 'ArrowUp':
          if (viewIndex !== 0) {
            const previousView = views[viewIndex - 1];
            const previousButton = document.getElementById(`${previousView.id}-button`);
            previousButton!.focus();
          }
          break;
        case 'ArrowRight':
          viewAction?.focus();
          break;
        case 'Tab':
        case 'Escape':
          event.preventDefault();
          setOpen(false);
          break;
        default:
      }
    };
    return (
      <ul ref={popoverContentRef} className={classes.dropdownList}>
        {errorOnAction}
        {groupedViews.map((group, groupIndex) => (
          <Fragment key={group.id}>
            {group.name && (
              <li className={groupIndex !== 0 ? classes.groupSpacing : undefined}>
                <p className={classes.groupName}>{group.name}</p>
              </li>
            )}
            {group.views.length === 0 && (
              <li className={classes.groupEmpty}>
                {group.empty || locale.get('dataComponents.emptyHeader')}
              </li>
            )}
            {group.views.map((view) => (
              <li key={`${view.id}-${view.name}`}>
                <ViewRow
                  onActionKeyDown={handleActionKeyDown}
                  onDelete={onDeleteSavedView}
                  onRename={onRenameSavedView}
                  onSetActive={() => {
                    onSetActiveSavedView({ view, onResolve: () => setOpen(false) });
                  }}
                  onSetDefault={onSetDefaultSavedView}
                  onViewKeyDown={handleViewKeyDown}
                  popoverRef={popoverContentRef}
                  view={view}
                />
              </li>
            ))}
          </Fragment>
        ))}
      </ul>
    );
  }, [
    classes.dropdownList,
    classes.groupEmpty,
    classes.groupName,
    classes.groupSpacing,
    errorOnAction,
    groupedViews,
    onDeleteSavedView,
    onRenameSavedView,
    onSetActiveSavedView,
    onSetDefaultSavedView,
    views,
  ]);

  const onSaveSavedViewActive = useCallback(
    () => onSaveSavedView({ view: activeView }),
    [activeView, onSaveSavedView]
  );

  /** Has an internal mouse event listener rather than useClickOutside utility
   * to avoid conflicts with the handleActionKeyDown and handleViewKeyDown functions here
   */
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const element = event.target;
      if (
        popoverContentRef?.current &&
        element instanceof Node &&
        !popoverContentRef.current.contains(element)
      )
        setOpen(false);
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [popoverContentRef]);

  return (
    <div className={classNames(classes.wrap, className)} id={htmlId}>
      <Popover
        className={classes.popover}
        content={popoverContent}
        disableA11yButton
        noArrow
        open={open}
        placement="bottom-start"
        popperOptions={popperOptions}
      >
        <button
          aria-expanded={open}
          className={classes.activeView}
          onClick={handleSavedViewsClick}
          type="button"
        >
          {diverged && <InfoCircleAshIcon className={classes.infoIcon} />}
          <span className={classes.activeViewName}>{activeView.name}</span>
          <CaretDownAshIcon />
        </button>
      </Popover>
      {errorOnSave}
      {(diverged || wasJustSaved) && !errorOnSave && (
        <SaveOrUpdateView
          canCreateNew={maxViewsCount ? maxViewsCount > views.length : true}
          canSave={!activeView.isStatic}
          onCreate={onCreateSavedView}
          onSave={onSaveSavedViewActive}
          suggestedName={suggestedName}
          wasJustSaved={wasJustSaved}
        />
      )}
    </div>
  );
};

export default SavedViews;
